import React, { useState } from 'react';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { FolderType, FileType, FolderWithCount } from '../AccountFiles';
import BemClass from '@upsales/components/Utils/bemClass';
import { getIconAndColor } from 'App/babel/helpers/file';
import { sortItems } from '../Helpers/helpers';
import T from 'Components/Helpers/translate';
import {
	EllipsisTooltip,
	Icon,
	Link,
	Table,
	TableColumn,
	TableHeader,
	TableRow,
	Text,
	Tooltip
} from '@upsales/components';
import FolderRow from './FolderRow';
import moment from 'moment';
import './ListView.scss';

type Props = {
	folders: FolderType[];
	files: FileType[];
	openFolder: (folderId: number, name: string) => void;
	deleteFolder: (id: number, totalFiles: number) => void;
	deleteFile: (file: FileType) => void;
	changeNameOfFolder: (id: number, name: string) => void;
	folderMap: Map<number, FolderWithCount>;
};

const ListView = ({ folders, files, openFolder, deleteFolder, deleteFile, changeNameOfFolder, folderMap }: Props) => {
	const classes = new BemClass('FilesListView');
	const [sortFolderField, setSortFolderField] = useState<keyof FolderType>('name');
	const [sortFileField, setSortFileField] = useState<keyof FileType>('name');
	const [foldersAsc, setFoldersAsc] = useState<boolean>(true);
	const [filesAsc, setFilesAsc] = useState<boolean>(true);

	const openRelated = (e: React.FormEvent, entity: string, entityId: number) => {
		e.stopPropagation();

		switch (entity) {
			case 'Contact':
				Tools.$state.go('contact.dashboard', { id: entityId });
				break;

			case 'Order':
			case 'Opportunity':
				Tools.$upModal.open('editOrder', { customerId: Tools.AppService.getCustomerId(), id: entityId });
				break;

			case 'Activity':
				Tools.$upModal.open('editActivity', { id: entityId });
				break;

			case 'Appointment':
				openEditAppointment({ id: entityId });
				break;
		}
	};

	const getRow = (file: FileType) => {
		const [iconName, color] = getIconAndColor(file?.extension);
		return (
			<TableRow
				className={classes.elem('file').b()}
				key={file.id}
				onClick={(e: React.MouseEvent<HTMLElement>) => {
					e.stopPropagation();

					Tools.File.download(file.id);
				}}
			>
				<TableColumn>
					<EllipsisTooltip title={file.name.match(/.{1,20}/g)?.join('\n')}>
						<Text className={classes.elem('file').elem('name').b()}>
							<Icon className={classes.elem('folder-icon').b()} color={color} name={iconName} />
							{file.name}
						</Text>
					</EllipsisTooltip>
				</TableColumn>
				<TableColumn>
					<Text>{`${Math.round(file.size / 10) / 100} Kb`}</Text>
				</TableColumn>
				<TableColumn>
					<Text>{moment(file.date).format('L')}</Text>
				</TableColumn>
				<TableColumn>
					<Text>{Tools.AppService.getUsers().find(u => u.id === file.userId)?.name ?? ''}</Text>
				</TableColumn>
				<TableColumn className={classes.elem('last-column').b()}>
					{file.related && file.related !== 'Client' ? (
						<Link
							onClick={e => {
								if (file.related && file.relatedId) {
									openRelated(e, file.related, file.relatedId);
								}
							}}
						>
							{file.related}
						</Link>
					) : (
						<div></div>
					)}
					<Tooltip className={classes.elem('delete-button').b()} title="Delete" position="left">
						{file.userRemovable ? (
							<Icon
								onClick={e => {
									e.stopPropagation();
									deleteFile(file);
								}}
								color="grey-6"
								name="trash-o"
							/>
						) : null}
					</Tooltip>
				</TableColumn>
			</TableRow>
		);
	};

	const folderColumns = [
		{ title: T('default.name'), sort: 'name' },
		{ title: T('file.folders') },
		{ title: T('file.files') },
		{ title: '' }
	];

	const fileColumns = [
		{ title: T('default.name'), sort: 'name' },
		{ title: T('file.fileSize'), sort: 'size', sortType: 'numeric' },
		{ title: T('file.uploadedDate'), sort: 'date', sortType: 'numeric' },
		{ title: T('file.uploader'), sort: 'userId' },
		{ title: T('file.relatedTo'), sort: 'related' }
	];

	const sortedFolders = sortItems(folders, sortFolderField, foldersAsc);

	const sortedFiles = sortItems(files, sortFileField, filesAsc);

	return (
		<div className={classes.b()}>
			{folders.length ? (
				<Table className={classes.elem('folderList').b()}>
					<TableHeader
						color="grey"
						columns={folderColumns}
						sorting={sortFolderField}
						asc={foldersAsc}
						onSortChange={sort => {
							setSortFolderField(sort.field as keyof FolderType);
							setFoldersAsc(sort.asc);
						}}
					/>
					{sortedFolders.map((folder: FolderType) => (
						<FolderRow
							folderMap={folderMap}
							key={folder.id}
							folder={folder}
							openFolder={openFolder}
							deleteFolder={deleteFolder}
							changeNameOfFolder={changeNameOfFolder}
						/>
					))}
				</Table>
			) : null}
			{files.length ? (
				<Table>
					<TableHeader
						color="grey"
						columns={fileColumns}
						sorting={sortFileField}
						asc={filesAsc}
						onSortChange={sort => {
							setSortFileField(sort.field as keyof FileType);
							setFilesAsc(sort.asc);
						}}
					/>
					{sortedFiles.map((file: FileType) => getRow(file))}
				</Table>
			) : null}
		</div>
	);
};

export default ListView;
